import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Upload, ArrowLeft, RotateCcw, Loader2, Calendar } from 'lucide-react';
import { useScanCard } from '@/hooks/useLeads';
import { useEvents } from '@/hooks/useEvents';
import { useAuthStore } from '@/stores/useAuthStore';
import { Card, Button } from '@/components/ui';
import CameraCapture from '@/components/CameraCapture';
import { useT } from '@/i18n/LanguageContext';

type PageState = 'select' | 'camera' | 'preview';

/** Compress large images to max 2048px on longest side, JPEG 80% quality */
function compressImage(dataUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const MAX_DIM = 2048;
      let { width, height } = img;

      if (width > MAX_DIM || height > MAX_DIM) {
        if (width > height) {
          height = Math.round(height * (MAX_DIM / width));
          width = MAX_DIM;
        } else {
          width = Math.round(width * (MAX_DIM / height));
          height = MAX_DIM;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Canvas context failed'));
      ctx.drawImage(img, 0, 0, width, height);
      const compressed = canvas.toDataURL('image/jpeg', 0.8);
      resolve(compressed.split(',')[1]);
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = dataUrl;
  });
}

export default function ScanCardPage() {
  const navigate = useNavigate();
  const scanCard = useScanCard();
  const selectedEventId = useAuthStore((s) => s.selectedEventId);
  const { data: events } = useEvents();
  const isTestEvent = !!events?.find((e) => e.id === selectedEventId)?.testMode;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const t = useT();

  const [state, setState] = useState<PageState>('select');
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [cameraAvailable] = useState(
    typeof navigator !== 'undefined' &&
      !!navigator.mediaDevices &&
      !!navigator.mediaDevices.getUserMedia,
  );

  // No event selected guard
  if (!selectedEventId) {
    return (
      <div className="p-4 md:p-6 flex items-center justify-center min-h-[400px]">
        <Card className="p-8 text-center max-w-sm">
          <Calendar size={32} className="mx-auto text-reevol-text-muted mb-3" />
          <h2 className="text-lg font-bold text-reevol-text mb-2">{t.common.selectEvent}</h2>
          <p className="text-sm text-reevol-text-muted mb-4">
            {t.common.chooseEventScan}
          </p>
          <Button onClick={() => navigate('/events')}>
            {t.common.goToEvents}
          </Button>
        </Card>
      </div>
    );
  }

  const handleCameraCapture = useCallback((base64: string) => {
    setImageBase64(base64);
    setPreviewUrl(`data:image/jpeg;base64,${base64}`);
    setState('preview');
  }, []);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input so the same file can be re-selected
    e.target.value = '';

    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const dataUrl = reader.result as string;
        setPreviewUrl(dataUrl);

        // Compress if large
        if (file.size > 2 * 1024 * 1024) {
          const compressed = await compressImage(dataUrl);
          setImageBase64(compressed);
        } else {
          setImageBase64(dataUrl.split(',')[1]);
        }

        setState('preview');
      };
      reader.readAsDataURL(file);
    } catch {
      setError(t.scan.failedReadFile);
    }
  };

  const handleScan = async () => {
    if (!imageBase64) return;
    setError('');

    try {
      const result = await scanCard.mutateAsync({ imageBase64, eventId: selectedEventId });
      navigate(`/enriching/${result.enrichmentJobId}`, { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t.scan.scanFailed);
    }
  };

  const handleRetake = () => {
    setImageBase64(null);
    setPreviewUrl(null);
    setError('');
    setState('select');
  };

  // Camera overlay (full screen)
  if (state === 'camera') {
    return (
      <CameraCapture
        onCapture={handleCameraCapture}
        onClose={() => setState('select')}
      />
    );
  }

  // Preview captured/uploaded image
  if (state === 'preview' && previewUrl) {
    return (
      <div className="p-4 md:p-6 max-w-lg mx-auto">
        <button
          onClick={handleRetake}
          className="flex items-center gap-1 text-sm text-reevol-text-muted hover:text-reevol-blue mb-4 transition-colors"
        >
          <ArrowLeft size={16} />
          {t.common.back}
        </button>

        <h1 className="text-xl font-bold text-reevol-text mb-1">{t.scan.reviewScan}</h1>
        <p className="text-sm text-reevol-text-muted mb-5">
          {t.scan.reviewScanDesc}
        </p>

        <Card className="p-4 mb-4">
          <img
            src={previewUrl}
            alt="Business card preview"
            className="w-full rounded-xl object-contain max-h-64 bg-reevol-bg"
          />
        </Card>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2 mb-4">{error}</p>
        )}

        <div className="flex gap-3">
          <button
            onClick={handleRetake}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-full border border-reevol-border text-sm font-medium text-reevol-text-secondary hover:bg-gray-50 transition-colors"
          >
            <RotateCcw size={16} />
            {t.scan.retake}
          </button>
          <Button
            fullWidth
            loading={scanCard.isPending}
            onClick={handleScan}
          >
            {t.scan.scanCard}
          </Button>
        </div>
      </div>
    );
  }

  // Method selection (initial state)
  return (
    <div className="p-4 md:p-6 max-w-lg mx-auto">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 text-sm text-reevol-text-muted hover:text-reevol-blue mb-4 transition-colors"
      >
        <ArrowLeft size={16} />
        {t.common.back}
      </button>

      <h1 className="text-xl font-bold text-reevol-text mb-1">{t.scan.title}</h1>
      <p className="text-sm text-reevol-text-muted mb-5">
        {t.scan.subtitle}
      </p>

      {isTestEvent && (
        <p className="text-xs text-amber-700 bg-amber-50 rounded-xl px-3 py-2 mb-4">
          {t.scan.testEventNotice}
        </p>
      )}

      <div className="space-y-3">
        {/* Take Photo option */}
        {cameraAvailable && (
          <Card
            className="p-5 cursor-pointer hover:border-reevol-blue/40 hover:shadow-md transition-all active:scale-[0.98]"
            onClick={() => setState('camera')}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-reevol-blue/10 flex items-center justify-center flex-shrink-0">
                <Camera size={22} className="text-reevol-blue" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm font-semibold text-reevol-text">{t.scan.takePhoto}</p>
                <p className="text-xs text-reevol-text-muted mt-0.5">{t.scan.takePhotoDesc}</p>
              </div>
            </div>
          </Card>
        )}

        {/* Upload Photo option */}
        <Card
          className="p-5 cursor-pointer hover:border-reevol-blue/40 hover:shadow-md transition-all active:scale-[0.98]"
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-reevol-blue/10 flex items-center justify-center flex-shrink-0">
              <Upload size={22} className="text-reevol-blue" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-sm font-semibold text-reevol-text">{t.scan.uploadPhoto}</p>
              <p className="text-xs text-reevol-text-muted mt-0.5">{t.scan.uploadPhotoDesc}</p>
            </div>
          </div>
        </Card>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileUpload}
          className="hidden"
        />

        {/* Divider */}
        <div className="flex items-center gap-3 py-2">
          <div className="flex-1 h-px bg-reevol-border" />
          <span className="text-xs text-reevol-text-muted font-medium">{t.common.or}</span>
          <div className="flex-1 h-px bg-reevol-border" />
        </div>

        {/* Manual entry link */}
        <button
          onClick={() => navigate('/new')}
          className="w-full text-center text-sm font-medium text-reevol-blue hover:text-reevol-blue/80 transition-colors py-2"
        >
          {t.scan.enterManually}
        </button>
      </div>

      {scanCard.isPending && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-6 shadow-xl flex items-center gap-3">
            <Loader2 size={20} className="animate-spin text-reevol-blue" />
            <span className="text-sm font-medium text-reevol-text">{t.scan.scanning}</span>
          </div>
        </div>
      )}
    </div>
  );
}
